import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { 
  useToast,
  Box,
} from '@chakra-ui/react';
import { getFilmCreditsCastsData, updateFilmCreditsCastsData } from '../../../services/Films';
import Form from '@rjsf/chakra-ui';
import validator from "@rjsf/validator-ajv8";
import { getDeletedValues, reverseTransformData, transformData } from '../../../util/helper';
import ImageWidget from '../../../CommonComponents/ImageWidget';
import TypeaheadWidget from '../../../CommonComponents/TypeaheadWidget';
import { getPersonalityAliasSuggestions } from '../../../services/Personality';
import { getLocationAliasSuggestions } from '../../../services/Location';
import { RjsfSubmitButton } from '../../../RJSFCommonSchemas';
import FormLoader from '../../../CommonComponents/FormLoader';
import { filmography } from '../../../constants/constants';
import { showErrorToast, showSuccessToast } from "../../../util/errorHandler";

const CreditsCasts = () => {
  const { alias } = useParams();
  const [basicFieldData, setBasicFieldData] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [processedData, setProcessedData] = useState({});
  const [fieldsSchema, setFieldsSchema] = useState({});
  const [quickSave, setQuickSave] = useState(false);
  const toast = useToast();


  const generateSchema = (fields) => {
    // Special title mappings for display
    const titleMappings = {
      "cameraman": filmography.CINEMATOGRAPHER
    };

    const getFieldTitle = (field) => {
      // Check if there's a custom mapping
      if (titleMappings[field]) {
        return titleMappings[field];
      }
      // Otherwise capitalize and format field name
      return field.charAt(0).toUpperCase() + field.slice(1).replace(/_/g, ' ');
    };

    const schema = {
      title: "Film Credits and Cast",
      description: "",
      type: "object",
      properties: {
        // Add the common properties directly in the schema.properties
        eastmancolor_by: {
          type: "string",
          title: "Eastmancolor",
          default: ""
        },
        credits_image_alias: {
          type: "array",
          title: "Credits Image Alias",
          items: {
          type: "object",
          properties: {
            image_alias: {
              type: "string",
              title: "Image Alias"
            },
            image: {
              "type": "string",
              "title": "Image",
            },
          }
        },
        cast_credits_image_alias: {
          type: "array",
          title: "Cast Credits Image Alias",
          items: {
            type: "object",
            properties: {
              image_alias: {
                type: "string",
                title: "Image Alias"
              },
              image: {
                "type": "string",
                "title": "Image",
              },
            }
          }
        },
        publicity_printer_and_designer_of_photo_sets_brouchure_booklets_folders_calendars: {
          type: "array",
          title: "Publicity Printer and Designer of Photo Sets, Brochure, Booklets, Folders, Calendars",
          items: {
            type: "object",
            properties: {
              alias: { type: "string", title: "Alias", default: ""},
            }
          }
        },
        playback_singer: {
          type: "array",
          title: "Playback Singer",
          items: {
            type: "object",
            properties: {
              alias: { type: "string", title: "Alias", default: ""}
            }
          }
        },
        costumes: {
          type: "array",
          title: "Costumes",
          items: {
            type: "object",
            properties: {
              alias: { type: "string", title: "Alias", default: ""}
            }
          }
        }
        }
      }
    };
  
    fields?.forEach(field => {
      schema.properties[field] = {
        type: "array",
        title: getFieldTitle(field),
        items: {
          type: "object",
          properties: {
            alias: { type: "string", title: "Alias" , default: ""},
            type: { type: "string", title: "Type" , default: field}
          }
        }
      };
    });
  
    return schema;
  };

  const roles   = [
    "advertising_agents",
    "animator",
    "art_director",
    "assistant_art_director",
    "assistant_camera",
    "assistant_cameraman",
    "assistant_choreographer",
    "assistant_costume_designer",
    "assistant_director",
    "assistant_editor",
    "assistant_graphic_designer",
    "assistant_in_director",
    "assistant_makeup_director",
    "assistant_music_director",
    "assistant_musician",
    "assistant_of_make_up_artist",
    "assistant_of_office_management",
    "assistant_of_sound",
    "assistant_office_in_charge",
    "assistant_office_incharge",
    "assistant_producer",
    "assistant_sound",
    "assistant_still_photographer",
    "assistants_in_audiography",
    "assistants_in_cinematography",
    "assistants_in_direction",
    "associate",
    "associate_art_director",
    "associate_camera",
    "associate_chief_electrician",
    "associate_choreographer",
    "associate_costume_designer",
    "associate_director",
    "associate_editor",
    "associate_graphic_designer",
    "associate_makeup_director",
    "associate_music_director",
    "associate_producer",
    "associate_production",
    "associate_song_recording_and_background_music",
    "associate_sound",
    "backdrop",
    "background_artist",
    "background_music",
    "background_score",
    "banners_poster_design_publicity_printing",
    "cameraman",
    "chief_assistant_director",
    "chief_electrician",
    "chief_production_manager",
    "choreographer",
    "consulting_producer",
    "controller_of_production",
    "coproducer",
    "costume_designer",
    "dancer",
    "dialogue_writer",
    "dialogues",
    "director",
    "director_of_photography",
    "director_of_sound",
    "disc_recorders",
    "dubbing",
    "editor",
    "equipment_manager",
    "executive_producer",
    "factory_manager",
    "fight_director",
    "graphic_designer",
    "hair_styles",
    "hairstyles",
    "junior_artistes_supplier",
    "leadCast",
    "leadCastRest",
    "line_producer",
    "linked_masterlists",
    "location_manager",
    "lyricist",
    "lyrics",
    "make_up_and_wig_maker",
    "make_up_artist",
    "make_up_director",
    "make_up_men",
    "makeup_and_wigmaker",
    "makeup_director",
    "music",
    "music_director",
    "music_recorder",
    "narrator",
    "office_in_charge_and_finance_controller",
    "office_incharge",
    "office_incharge_and_finance_controller",
    "office_manager",
    "poster_designer",
    "presented_by_institution",
    "presented_by_personality",
    "printer",
    "prints_by",
    "processed_at",
    "processing",
    "produced_at",
    "produced_directed_and_edited_by",
    "producer",
    "production_and_factory",
    "production_assistant",
    "production_designer",
    "production_executive",
    "production_factory",
    "production_manager",
    "projection",
    "publicity_designer",
    "publicity_in_charge",
    "publicity_incharge",
    "publicity_liasion_officer",
    "re_recorder",
    "re_recording",
    "recorded_on",
    "screenplay",
    "script",
    "script_writer",
    "secretary",
    "secretary_to_office",
    "senior_artistes_costumes",
    "song_recording_and_background_music",
    "sound",
    "sound_designer",
    "sound_director",
    "sound_editor",
    "sound_mixer",
    "sound_recording",
    "special_assistant",
    "special_effects",
    "still_photographer",
    "story_screenplay_and_dialogue",
    "storyWriter",
    "studio_manager",
    "supervising_producer",
    "trick_photography",
    "voice",
    "wardrobe",
    "banner",
]

  const dynamicSchema = roles.reduce((acc, role) => {
    acc[role] = {
      items: {
        alias: {
          "ui:widget": "personalityAliasTypeahead", // Use the custom TypeaheadWidget
        },
        type: {
          "ui:widget": "hidden" // Hide the type field in UI
        }
      },
    };
    return acc;
  }, {});

 const fetchLocationAliasSuggestions = async (inputValue) => {

    const fetchedMasterListAliasSuggestions = await loadLocationAliasSuggestions(inputValue);

    // Ensure inputValue is string and trimmed
    if (!inputValue || typeof inputValue !== "string" || !fetchedMasterListAliasSuggestions) {
      return [];
    }

    return fetchedMasterListAliasSuggestions?.map((item) => ({
      label: item.alias,
      value: item.alias
    }));
  };

  const loadLocationAliasSuggestions = async (inputValue) => {
    try {
      const result = await getLocationAliasSuggestions(20, 1, inputValue);
      return result?.data?.data || [];
    } catch (error) {
            showErrorToast(toast, "Failed to load document types");
    }
  };


  const uiSchema = {
    ...RjsfSubmitButton(isSubmitting),
    "credits_image_alias": {
      items: {
        image: {
          "ui:widget": ImageWidget,
        },
        type: {
          "ui:widget": "hidden" // Hide the type field in UI
        }
      },
    },
    "cast_credits_image_alias": {
      items: {
        image: {
          "ui:widget": ImageWidget,
        },
      },
    },
    "publicity_printer_and_designer_of_photo_sets_brouchure_booklets_folders_calendars": {
      items: {
        alias: {
          "ui:widget": "personalityAliasTypeahead",
        },
        type: {
          "ui:widget": "hidden" // Hide the type field in UI
        }
      },
    },
    "playback_singer": {
      items: {
        alias: {
          "ui:widget": "personalityAliasTypeahead",
        },
        type: {
          "ui:widget": "hidden" // Hide the type field in UI
        }
      },
    },
    "costumes": {
      items: {
        alias: {
          "ui:widget": "personalityAliasTypeahead",
        },
        type: {
          "ui:widget": "hidden" // Hide the type field in UI
        }
      },
    },
    "eastmancolor_by": { "ui:emptyValue": "" },
    studio: {
      items: {
        alias: {
          "ui:widget": "locationAliasTypeahead",
        },
        type: {
          "ui:widget": "hidden" // Hide the type field in UI
        }
      },
      'ui:classNames': "chakra-form__label"
    },
    ...dynamicSchema
  };


  const formData = processedData;

  const fetchPersonalityAliasSuggestions = async (inputValue) => {

    const fetchedMasterListAliasSuggestions = await loadPersonalityAliasSuggestions(inputValue);

    // Ensure inputValue is string and trimmed
    if (!inputValue || typeof inputValue !== "string" || !fetchedMasterListAliasSuggestions) {
      return [];
    }

    return fetchedMasterListAliasSuggestions?.map((item) => ({
      label: item.alias,
      value: item.alias
    }));
  };

  const loadPersonalityAliasSuggestions = async (inputValue) => {
    try {
      const result = await getPersonalityAliasSuggestions(20, 1, inputValue);
      return result?.data?.data || [];
    } catch (error) {
            showErrorToast(toast, "Failed to load document types");
    }
  };

  const widgets = {
    locationAliasTypeahead: (props) => (
      <TypeaheadWidget
        {...props}
        fetchOptions={fetchLocationAliasSuggestions}
      />
    ),
    personalityAliasTypeahead: (props) => (
      <TypeaheadWidget
        {...props}
        fetchOptions={fetchPersonalityAliasSuggestions}
      />
    ),
  };

  useEffect(() => {
    const SKIP_FIELDS = ['credits_image_alias', 'cast_credits_image_alias', 'eastmancolor_by', 'studios'];
    const FIELDS_TO_TRANSFORM = ['credits_image_alias', 'cast_credits_image_alias'];
  
    const getPrefaceData = async () => {
      if (!alias) return;
  
      try {
        setIsLoading(true);
        const result = await getFilmCreditsCastsData(alias);
        
        if (!result) {
          throw new Error('No data received from API');
        }
  
        // Process all data transformations in parallel
        const [fieldsSchema, processedData] = await Promise.all([
          // Generate schema for non-empty fields that aren't skipped
          generateSchema(
            Object.keys(result).filter(key => 
              !SKIP_FIELDS.includes(key)
            )
          ),
          // Transform specified fields
          transformData(result, FIELDS_TO_TRANSFORM)
        ]);
  
        // Update all states at once to minimize re-renders
        setFieldsSchema(fieldsSchema);
        setProcessedData(processedData);
        setBasicFieldData(result);
  
      } catch (error) {
        const errorMessage = error.message || 'Failed to fetch film data';
        console.error('Error in getPrefaceData:', {
          error,
          alias,
          message: errorMessage
        });
        
        showErrorToast(toast, errorMessage);
      } finally {
        setIsLoading(false);
        setQuickSave(false);
      }
    };
  
    getPrefaceData();
  }, [alias, toast, quickSave]);


  // Submit updated data
  const handleSubmit = async (formData) => {
    try {
      if (alias) {
        setIsSubmitting(true);
        const fieldsToTransform = [
          'credits_image_alias',
          'cast_credits_image_alias'
        ];
    
        const updatedFormData = reverseTransformData(formData, fieldsToTransform);
        const deletedValues = getDeletedValues(basicFieldData, updatedFormData);
        const result = await updateFilmCreditsCastsData(alias, updatedFormData, deletedValues);

        if (result?.status === 200)
        {
          setQuickSave(prev => !prev);
                        showSuccessToast(toast, "Data updated successfully");
        } else {
          // Backend reported a real failure (Neo4j write error) — surface it
          // instead of leaving the user with no feedback at all.
          showErrorToast(toast, result?.errors?.[0] || "Failed to update film data");
        }
      }
    } catch (error) {
      console.error('Error updating film data:', error);
            showErrorToast(toast, "Failed to update film data");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box maxW="1200px">
      {isLoading ? (
        <FormLoader />
      ) : (
        <Form
          schema={fieldsSchema}
          uiSchema={uiSchema}
          formData={formData}
          validator={validator}
          onSubmit={(e) => handleSubmit(e.formData)}
          widgets={widgets}
          disabled={isSubmitting}
        >
        </Form>
      )}
    </Box>
  );
};

export default CreditsCasts;